import authService from './authService';
import { 
  Product, 
  ProductFormData, 
  ProductSearchModel, 
  ProductWithProviders,
  UpdateStockRequest,
  SkuValidationResponse,
  CostLayersResponse,
  ProductPendingOrdersResponse,
  RecalculateStockResponse,
  ProductNameGroup,
  ProductNameGroupsFilter,
  ProductDimensionsPayload
} from '../types/product';
import { PaginatedResponse, ApiResponse } from '../types/common';
import { PaginationInfo } from '../types/stockAnalytics';

class ProductService {
  constructor() {
    // Configuration will be loaded lazily when needed
  }

  /**
   * Get authenticated API client
   */
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * List products with pagination
   */
  async getProducts(searchModel: ProductSearchModel): Promise<PaginatedResponse<Product>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.searchSku && { sku: searchModel.searchSku }),
      ...(searchModel.minPrice && { minPrice: searchModel.minPrice.toString() }),
      ...(searchModel.maxPrice && { maxPrice: searchModel.maxPrice.toString() }),
      ...(searchModel.inStock !== undefined && { inStock: searchModel.inStock.toString() }),
      ...(searchModel.isFragile !== undefined && { isFragile: searchModel.isFragile.toString() }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    // Adicionar novos filtros
    if (searchModel.is_active !== undefined) {
      params.append('is_active', searchModel.is_active.toString());
    }
    if (searchModel.in_stock) {
      params.append('stock_status', 'in_stock');
    }
    if (searchModel.low_stock) {
      params.append('stock_status', 'low_stock');
    }
    if (searchModel.out_of_stock) {
      params.append('stock_status', 'out_of_stock');
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/products?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao carregar produtos');
    }

    const jsonResponse = await response.json();
    return jsonResponse;
  }

  /**
   * Get product by ID
   */
  async getProductById(id: number): Promise<Product> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/products/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Produto não encontrado');
      }
      throw new Error('Falha ao carregar produto');
    }

    const data: ApiResponse<Product> = await response.json();
    return data.data!;
  }

  /**
   * Get product with providers
   */
  async getProductWithProviders(id: number): Promise<ProductWithProviders> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/products/${id}/with-providers`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Produto não encontrado');
      }
      throw new Error('Falha ao carregar produto com fornecedores');
    }

    const data: ApiResponse<ProductWithProviders> = await response.json();
    return data.data!;
  }

  /**
   * Validate SKU availability
   */
  async validateSku(sku: string): Promise<SkuValidationResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/products/validate-sku', { sku });

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error('SKU é obrigatório');
      }
      throw new Error('Erro ao validar SKU');
    }

    const data: ApiResponse<SkuValidationResponse> = await response.json();
    return data.data!;
  }

  /**
   * Create product
   */
  async createProduct(productData: ProductFormData): Promise<Product> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/products', productData);

    if (!response.ok) {
      if (response.status === 409) {
        const errorData = await response.json();
        throw new Error(errorData.message || `SKU '${productData.sku}' já está em uso. Por favor, utilize um SKU único.`);
      }
      // Tenta extrair mensagem de erro detalhada do backend
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
        if (errorData.errors) {
          const msgs = Object.entries(errorData.errors)
            .map(([field, msg]) => `${field}: ${msg}`)
            .join('; ');
          throw new Error(`Erro de validação: ${msgs}`);
        }
      } catch (e) {
        if (e instanceof Error && e.message !== 'Falha ao criar produto') throw e;
      }
      throw new Error('Falha ao criar produto');
    }

    const data: ApiResponse<Product> = await response.json();
    return data.data!;
  }
  /**
   * Update product
   */
  async updateProduct(id: number, productData: Partial<ProductFormData>): Promise<Product> {
    console.log('🔄 ProductService.updateProduct called');
    console.log('🔄 Product ID:', id);
    console.log('🔄 Product data to update:', productData);
    
    const apiClient = await this.getApiClient();
    const url = `/products/${id}`;
    
    console.log('🔄 Making PUT request to:', url);
    
    const response = await apiClient.put(url, productData);
    
    console.log('🔄 Update response status:', response.status);
    console.log('🔄 Update response ok:', response.ok);

    if (!response.ok) {
      console.error('❌ Update failed with status:', response.status);
      if (response.status === 404) {
        throw new Error('Produto não encontrado');
      }
      if (response.status === 409) {
        const errorData = await response.json();
        throw new Error(errorData.message || `SKU já está em uso. Por favor, utilize um SKU único.`);
      }
      const errorText = await response.text();
      console.error('❌ Error response body:', errorText);
      throw new Error('Falha ao atualizar produto');
    }

    const data: ApiResponse<Product> = await response.json();
    console.log('✅ Product update successful:', data);
    return data.data!;
  }

  /**
   * Delete product
   */
  async deleteProduct(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/products/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Produto não encontrado');
      }
      throw new Error('Falha ao excluir produto');
    }
  }

  /**
   * Search products by term
   */
  async searchProducts(query: string, page: number = 1, pageSize: number = 20, filters: any = {}): Promise<{ products: Product[], pagination?: any }> {
    const params = new URLSearchParams({ 
      q: query,
      page: page.toString(),
      page_size: pageSize.toString()
    });

    // Adicionar filtros aos parâmetros
    if (filters.is_active !== undefined) {
      params.append('is_active', filters.is_active.toString());
    }
    if (filters.in_stock) {
      params.append('stock_status', 'in_stock');
    }
    if (filters.low_stock) {
      params.append('stock_status', 'low_stock');
    }
    if (filters.out_of_stock) {
      params.append('stock_status', 'out_of_stock');
    }

    const apiClient = await this.getApiClient();
    const url = `/products/search?${params}`;
    
    const response = await apiClient.get(url);

    if (!response.ok) {
      throw new Error('Falha ao pesquisar produtos');
    }

    const jsonResponse = await response.json();
    
    // Handle different possible response structures
    let productsArray: Product[] = [];
    let paginationData = null;
    
    if (Array.isArray(jsonResponse)) {
      productsArray = jsonResponse;
    } else if (jsonResponse && typeof jsonResponse === 'object') {
      if (Array.isArray(jsonResponse.data)) {
        productsArray = jsonResponse.data;
      } else if (jsonResponse.data && typeof jsonResponse.data === 'object' && Array.isArray(jsonResponse.data.data)) {
        productsArray = jsonResponse.data.data;
        paginationData = jsonResponse.data.pagination;
      } else if (jsonResponse.data && typeof jsonResponse.data === 'object' && Array.isArray(jsonResponse.data.items)) {
        productsArray = jsonResponse.data.items;
        paginationData = jsonResponse.data.pagination;
      } else if (jsonResponse.data && typeof jsonResponse.data === 'object') {
        for (const key of Object.keys(jsonResponse.data)) {
          if (Array.isArray(jsonResponse.data[key])) {
            productsArray = jsonResponse.data[key];
            break;
          }
        }
      } else {
        for (const key of Object.keys(jsonResponse)) {
          if (Array.isArray(jsonResponse[key])) {
            productsArray = jsonResponse[key];
            break;
          }
        }
      }
    }
    
    return {
      products: productsArray,
      pagination: paginationData
    };
  }

  /**
   * Look up stock data for multiple SKUs.
   * Returns a map of SKU → { product_id, stock_quantity, stock_quantity_available, min_stock_quantity }.
   * Uses the batch stock endpoint for efficiency. Falls back to individual search if batch is unavailable.
   */
  async getStockBySku(skus: string[]): Promise<Record<string, { product_id: number; stock_quantity: number | null; stock_quantity_available: number | null; min_stock_quantity: number | null }>> {
    const unique = [...new Set(skus.filter(Boolean))];
    if (unique.length === 0) return {};

    // Try batch endpoint first
    try {
      const apiClient = await this.getApiClient();
      const res = await apiClient.post('/products/stock-batch', { skus: unique });
      if (res.ok) {
        const json = await res.json();
        const items: Array<{ sku: string; product_id: number; stock_quantity: number | null; stock_quantity_available: number | null; min_stock_quantity: number | null }> = json.data ?? [];
        const results: Record<string, { product_id: number; stock_quantity: number | null; stock_quantity_available: number | null; min_stock_quantity: number | null }> = {};
        for (const item of items) {
          results[item.sku] = {
            product_id: item.product_id,
            stock_quantity: item.stock_quantity ?? null,
            stock_quantity_available: item.stock_quantity_available ?? null,
            min_stock_quantity: item.min_stock_quantity ?? null,
          };
        }
        return results;
      }
    } catch {
      // Batch endpoint not available, fall back to individual lookups
    }

    // Fallback: search all unique SKUs in parallel (limited concurrency)
    const results: Record<string, { product_id: number; stock_quantity: number | null; stock_quantity_available: number | null; min_stock_quantity: number | null }> = {};

    const batchSize = 5;
    for (let i = 0; i < unique.length; i += batchSize) {
      const batch = unique.slice(i, i + batchSize);
      const promises = batch.map(async (sku) => {
        try {
          const { products } = await this.searchProducts(sku, 1, 5);
          // Find exact SKU match
          const match = products.find(p => p.sku === sku);
          if (match) {
            results[sku] = {
              product_id: match.id,
              stock_quantity: match.stock_quantity ?? null,
              stock_quantity_available: match.stock_quantity_available ?? null,
              min_stock_quantity: match.min_stock_quantity ?? null,
            };
          }
        } catch {
          // Ignore individual lookup failures
        }
      });
      await Promise.all(promises);
    }

    return results;
  }

  /**
   * Download product labels as PDF
   */
  async downloadLabels(id: number, quantity: number = 1): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/products/${id}/labels?quantity=${quantity}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar etiquetas (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `etiquetas-produto-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  /**
   * Update product stock
   */
  async updateProductStock(
    id: number,
    stockData: UpdateStockRequest
  ): Promise<Product> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/products/${id}/stock`, stockData);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Produto não encontrado');
      }
      throw new Error('Falha ao atualizar estoque do produto');
    }

    const data: ApiResponse<Product> = await response.json();
    return data.data!;
  }

  /**
   * Get cost layers for a product (FIFO)
   */
  async getCostLayers(productId: number): Promise<CostLayersResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/products/${productId}/cost-layers`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto não encontrado');
      throw new Error('Falha ao buscar camadas de custo');
    }
    const data: ApiResponse<CostLayersResponse> = await response.json();
    return data.data!;
  }

  /**
   * Get pending orders reserving stock for a specific product
   */
  async getPendingOrders(productId: number): Promise<ProductPendingOrdersResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/products/${productId}/pending-orders`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto não encontrado');
      throw new Error('Falha ao buscar pedidos pendentes');
    }
    const data: ApiResponse<ProductPendingOrdersResponse> = await response.json();
    return data.data!;
  }

  /**
   * Recalculate stock_quantity_available for a single product
   */
  async recalculateStock(productId: number): Promise<RecalculateStockResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/products/${productId}/recalculate-stock`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto não encontrado');
      throw new Error('Falha ao recalcular estoque');
    }
    const data: ApiResponse<RecalculateStockResponse> = await response.json();
    return data.data!;
  }

  /**
   * Recalculate stock_quantity_available for all active products (async background job)
   */
  async recalculateStockAll(): Promise<string> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/products/recalculate-stock-all');
    if (!response.ok) {
      throw new Error('Falha ao iniciar recálculo de estoque em massa');
    }
    const data = await response.json();
    return data.message || 'Recálculo iniciado em segundo plano';
  }


  // ─── Foto do Projeto (produto personalizado) ────────────────────
  // O container do blob é PRIVADO: o binário só sai pelo proxy do backend —
  // nunca usar URL de blob como src de <img>.

  /** Envia (ou substitui) a foto do projeto aprovado. Campo multipart: project_photo. */
  async uploadProjectPhoto(productId: number, photo: File): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('project_photo', photo);

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/products/${productId}/project-photo`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as { message?: string }));
      throw new Error(body.message || `Erro ao enviar a foto do projeto (${response.status})`);
    }
  }

  /** Remove a foto do projeto do produto. */
  async deleteProjectPhoto(productId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/products/${productId}/project-photo`);
    if (!response.ok) throw new Error('Erro ao remover a foto do projeto');
  }

  /** Busca o binário da foto do projeto via proxy e devolve um object URL (revogar após o uso). */
  async getProjectPhotoFile(productId: number): Promise<string> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/products/${productId}/project-photo/file`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      throw new Error(`Erro ao carregar a foto do projeto (${response.status})`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /**
   * Lista produtos agrupados pelo nome normalizado (trim, sem distinção de
   * caixa/acento). A paginação conta GRUPOS DE NOME, não produtos: um grupo
   * nunca vem partido entre páginas.
   */
  async getProductNameGroups(
    filter: ProductNameGroupsFilter
  ): Promise<{ groups: ProductNameGroup[]; pagination: PaginationInfo | null }> {
    const params = new URLSearchParams();
    params.set('page', String(filter.page ?? 1));
    params.set('limit', String(filter.limit ?? 20));
    if (filter.q) params.set('q', filter.q);
    if (filter.duplicatesOnly) params.set('duplicates_only', 'true');
    if (filter.isActive !== undefined) params.set('is_active', String(filter.isActive));

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/products/by-name?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar os produtos por nome');

    const body = await response.json();
    return { groups: body.data ?? [], pagination: body.pagination ?? null };
  }

  /**
   * Atualiza SOMENTE as dimensões do produto. Campo omitido não é alterado.
   * Funciona também em produto inativo — que é onde estão quase todos os
   * nomes duplicados.
   */
  async updateProductDimensions(id: number, payload: ProductDimensionsPayload): Promise<Product> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/products/${id}/dimensions`, payload);

    if (!response.ok) {
      let message = 'Falha ao salvar as dimensões';
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
        else if (body?.error) message = body.error;
      } catch {
        // resposta sem corpo JSON: fica a mensagem genérica
      }
      throw new Error(message);
    }

    const body: ApiResponse<Product> = await response.json();
    return body.data!;
  }
}

// Singleton instance
export const productService = new ProductService();
export default productService;